import { Button } from "@fieldwork/ui/components/button";
import { Input } from "@fieldwork/ui/components/input";
import { Textarea } from "@fieldwork/ui/components/textarea";
import { cn } from "@fieldwork/ui/lib/utils";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";

import { NotConnected, useServer } from "@/components/server-hooks";
import { Note, ServerPage } from "@/components/server-shell";
import { Chip, Field, Select } from "@/components/ui-bits";
import { useAgentAction, useAgentQuery } from "@/lib/act";

export const Route = createFileRoute("/_auth/servers/$id/projects")({
  component: ProjectsTab,
});

type Project = { slug: string; name: string; status: string; company: string; role: string; plane: string | null; content: string };
type Company = { file: string; name: string; content: string };
type Ctx = { projects: Project[]; companies: Company[]; channels: string; people: number; templates: { project: string; company: string } };
type Channel = { source: string; key: string };

const SOURCES: [string, string][] = [
  ["slack", "<workspace>/#<channel>, or the workspace name for all of it"],
  ["gmail", "<inbox>, from:<domain>, or <inbox>/label:<label>"],
  ["telegram", "chat id or exact chat name"],
  ["github", "owner/repo"],
  ["notion", "<workspace account>"],
  ["meeting", "<gmail or fireflies account>"],
];

const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);

type Sel = { kind: "project"; slug: string } | { kind: "company"; file: string } | { kind: "new-project" } | { kind: "new-company" } | { kind: "channels" };

const label11 = "font-mono text-[11px] uppercase tracking-[0.08em] text-[#A8A59D]";

function ProjectsTabBody({ sel, setSel }: { sel: Sel | null; setSel: (s: Sel) => void }) {
  const { id } = Route.useParams();
  const server = useServer(id).data;
  const connected = server?.hub.connected;
  const ctx = useAgentQuery<Ctx>(id, "context_list", {}, !!connected);
  const c = ctx.data;
  useEffect(() => {
    if (c && !sel) {
      const first = c.projects.find((p) => p.status !== "done");
      setSel(first ? { kind: "project", slug: first.slug } : { kind: "new-project" });
    }
  }, [c, sel, setSel]);
  if (!connected) return <NotConnected />;
  if (ctx.error) return <p className="text-sm text-[#F48A80]">{ctx.error.message}</p>;
  if (!c) return <p className="text-sm text-[#A8A59D]">Loading…</p>;
  const active = c.projects.filter((p) => p.status !== "done");
  const done = c.projects.filter((p) => p.status === "done");
  const is = (k: Sel["kind"], key?: string) =>
    sel?.kind === k && (!key || (sel.kind === "project" && sel.slug === key) || (sel.kind === "company" && sel.file === key));
  const project = sel?.kind === "project" ? c.projects.find((p) => p.slug === sel.slug) : undefined;
  const company = sel?.kind === "company" ? c.companies.find((x) => x.file === sel.file) : undefined;

  const projectCard = (p: Project) => (
    <button
      key={p.slug}
      type="button"
      aria-current={is("project", p.slug) || undefined}
      onClick={() => setSel({ kind: "project", slug: p.slug })}
      className={cn(
        "flex flex-col gap-1 rounded-md px-4 py-3.5 text-left",
        is("project", p.slug) ? "bg-[#222220] shadow-[inset_0_0_0_1px_#FF4F1A]" : "bg-[#1A1A19] shadow-[inset_0_0_0_1px_#2A2927] hover:shadow-[inset_0_0_0_1px_#3A3935]",
      )}
    >
      <span className="flex justify-between gap-2">
        <span className="font-semibold">{p.name}</span>
        {p.plane && <span className="font-mono text-xs text-[#A8A59D]">{p.plane}</span>}
      </span>
      <span className="text-[13px] text-[#A8A59D]">{p.company || p.role || p.slug}</span>
    </button>
  );

  return (
    <div className="flex flex-wrap items-start gap-7">
      <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-6">
        <section aria-labelledby="p-list" className="flex flex-col gap-2">
          <h2 id="p-list" className={cn("m-0 mb-1 text-[13px]", label11)}>
            Active · {active.length}
          </h2>
          {active.map(projectCard)}
          {done.length > 0 && (
            <details className="px-1 py-2" open={done.some((p) => is("project", p.slug))}>
              <summary className="cursor-pointer font-mono text-xs text-[#A8A59D]">{done.length} finished projects</summary>
              <div className="mt-2 flex flex-col gap-2">{done.map(projectCard)}</div>
            </details>
          )}
        </section>

        <section aria-labelledby="c-list" className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <h2 id="c-list" className={cn("m-0 text-[13px]", label11)}>
              Company notes · {c.companies.length}
            </h2>
            <Button size="xs" variant="outline" onClick={() => setSel({ kind: "new-company" })}>
              Add
            </Button>
          </div>
          <Note>How each organisation works: who decides, where tasks live, reply norms. Its rules override the defaults for that company's work.</Note>
          <div className="flex flex-col">
            {c.companies.map((x) => (
              <button
                key={x.file}
                type="button"
                onClick={() => setSel({ kind: "company", file: x.file })}
                className={cn(
                  "flex justify-between border-t border-[#262523] py-2.5 text-left last:border-b",
                  is("company", x.file) ? "text-[#FF4F1A]" : "hover:text-[#FF4F1A]",
                )}
              >
                <span>{x.name}</span>
                <span className="font-mono text-xs text-[#A8A59D]">{x.file}</span>
              </button>
            ))}
          </div>
        </section>

        <button
          type="button"
          onClick={() => setSel({ kind: "channels" })}
          className={cn("self-start font-mono text-[13px]", is("channels") ? "text-[#FF4F1A]" : "hover:text-[#FF4F1A]")}
        >
          Advanced: edit the whole channel map →
        </button>
      </div>

      <section className="flex min-w-0 flex-[3_1_560px] flex-col gap-5 rounded-md bg-[#1A1A19] px-7 py-6 shadow-[inset_0_0_0_1px_#2A2927]">
        {sel?.kind === "new-project" && (
          <>
            <EditorTitle title="New project" />
            <NewProject id={id} ctx={c} onDone={(slug) => setSel({ kind: "project", slug })} />
          </>
        )}
        {project && <ProjectEditor key={project.slug} id={id} p={project} />}
        {(sel?.kind === "new-company" || company) && (
          <CompanyEditor key={company?.file ?? "__new"} id={id} c={company} template={c.templates.company} onSaved={(file) => setSel({ kind: "company", file })} />
        )}
        {sel?.kind === "channels" && <ChannelsEditor id={id} text={c.channels} />}
      </section>
    </div>
  );
}

function EditorTitle({ title, badge, right }: { title: ReactNode; badge?: ReactNode; right?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-2.5">
        <h2 className="m-0 font-mono text-xl font-bold">{title}</h2>
        {badge}
      </div>
      {right}
    </div>
  );
}

function ProjectEditor({ id, p }: { id: string; p: Project }) {
  const [text, setText] = useState(p.content);
  const [channels, setChannels] = useState<Channel[]>([]);
  const save = useAgentAction<void>(
    id,
    "project_save",
    () => ({ slug: p.slug, content: text, channels: channels.filter((c) => c.key.trim()), create_plane: false }),
    { refresh: ["context_list"], success: "Saved; the next run uses it" },
  );
  const archive = useAgentAction<boolean>(id, "project_archive", (restore) => ({ slug: p.slug, restore }), {
    refresh: ["context_list"],
    success: p.status === "done" ? "Reopened" : "Marked finished",
  });
  const fixed = ["inbox", "personal"].includes(p.slug);
  return (
    <>
      <EditorTitle
        title={p.name}
        badge={
          <>
            <Chip value={p.status === "done" ? "neutral" : "ok"} label={p.status || "active"} />
            {p.plane && <span className="font-mono text-xs text-[#A8A59D]">Plane {p.plane}</span>}
          </>
        }
      />
      <label className="flex flex-col gap-1.5">
        <span className={label11}>The note the agent reads</span>
        <Textarea
          rows={18}
          className="bg-[#0E0E0D] px-4 py-3.5 font-mono text-[13px] leading-[1.7]"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <span className="text-xs text-[#A8A59D]">People, where it's discussed, current priorities, notes for the agent.</span>
      </label>
      <ChannelRows channels={channels} setChannels={setChannels} />
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#2A2927] pt-4">
        {fixed ? (
          <span />
        ) : (
          <Button variant="ghost" disabled={archive.isPending} onClick={() => archive.mutate(p.status === "done")}>
            {p.status === "done" ? "Reopen" : "Mark finished"}
          </Button>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <Note>The next run uses it · saved as a git commit</Note>
          <Button disabled={save.isPending} onClick={() => save.mutate(undefined, { onSuccess: () => setChannels([]) })}>
            Save
          </Button>
        </div>
      </div>
    </>
  );
}

function ChannelRows({
  channels,
  setChannels,
  legend = "Map more channels to this project",
}: {
  channels: Channel[];
  setChannels: (c: Channel[]) => void;
  legend?: string;
}) {
  return (
    <fieldset className="m-0 flex flex-col gap-2.5 border-0 p-0">
      <legend className={cn("pb-2", label11)}>{legend}</legend>
      {channels.map((c, i) => (
        <div key={i} className="flex gap-2">
          <Select
            aria-label="Source"
            className="w-[130px] shrink-0"
            value={c.source}
            onChange={(e) => setChannels(channels.map((x, j) => (j === i ? { ...x, source: e.target.value } : x)))}
          >
            {SOURCES.map(([s]) => (
              <option key={s} value={s}>
                {s[0]!.toUpperCase() + s.slice(1)}
              </option>
            ))}
          </Select>
          <Input
            aria-label="Channel"
            className="font-mono text-[13px]"
            placeholder={SOURCES.find(([s]) => s === c.source)?.[1]}
            value={c.key}
            onChange={(e) => setChannels(channels.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))}
          />
          <Button variant="ghost" aria-label="Remove channel" className="w-10 px-0" onClick={() => setChannels(channels.filter((_, j) => j !== i))}>
            ×
          </Button>
        </div>
      ))}
      <Button size="sm" variant="outline" className="self-start" onClick={() => setChannels([...channels, { source: "slack", key: "" }])}>
        + Add a channel
      </Button>
    </fieldset>
  );
}

function NewProject({ id, ctx, onDone }: { id: string; ctx: Ctx; onDone: (slug: string) => void }) {
  const [f, setF] = useState({ name: "", slug: "", company: "", role: "", people: "", where: "", priorities: "", notes: "", plane: "" });
  const [channels, setChannels] = useState<Channel[]>([{ source: "slack", key: "" }]);
  const [createPlane, setCreatePlane] = useState(true);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const slug = f.slug || slugify(f.name);
  const lines = (s: string, prefix = "- ") => s.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => prefix + l.replace(/^[-*]\s*/, "")).join("\n") || `${prefix}`;
  const content = `# ${f.name.trim()}

- slug: ${slug}
- company / client: ${f.company.trim()}
- plane project: ${f.plane.trim().toUpperCase()}
- status: active
- my role: ${f.role.trim()}

## People
${lines(f.people)}

## Where it's discussed
${lines(f.where)}

## Current priorities
${lines(f.priorities, "1. ")}

## Notes for the agent
${lines(f.notes)}
`;
  const save = useAgentAction<void, { notes: string[] }>(
    id,
    "project_save",
    () => ({ slug, content, channels: channels.filter((c) => c.key.trim()), create_plane: createPlane }),
    { refresh: ["context_list"], success: (r) => ["Project created", ...r.notes].join(" · ") },
  );
  const taken = ctx.projects.some((p) => p.slug === slug);
  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(undefined, { onSuccess: () => onDone(slug) });
      }}
    >
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(220px,100%),1fr))] gap-4">
        <Field label="Project name">
          <Input className="h-11" value={f.name} onChange={set("name")} required />
        </Field>
        <Field label="Short id" hint={taken ? "already used" : "used in the channel map"}>
          <Input className="h-11" value={slug} onChange={(e) => setF({ ...f, slug: slugify(e.target.value) })} required />
        </Field>
        <Field label="Plane key (optional)" hint="e.g. ACME; else derived from the id">
          <Input className="h-11" value={f.plane} onChange={set("plane")} maxLength={12} />
        </Field>
        <Field label="Company / client">
          <Input className="h-11" value={f.company} onChange={set("company")} />
        </Field>
        <Field label="Your role">
          <Input className="h-11" value={f.role} onChange={set("role")} placeholder="e.g. advisor on the API launch" />
        </Field>
      </div>
      <Field label="People · one per line" hint="Name – role, e.g. “Sarah Lee – my contact at Acme”">
        <Textarea rows={3} value={f.people} onChange={set("people")} />
      </Field>
      <Field label="Where it's discussed · one per line" hint="e.g. slack: acme/#launch, telegram: Acme x Me">
        <Textarea rows={2} value={f.where} onChange={set("where")} />
      </Field>
      <Field label="Current priorities · one per line">
        <Textarea rows={2} value={f.priorities} onChange={set("priorities")} />
      </Field>
      <Field label="Notes for the agent · one per line" hint="e.g. “Invoices from Acme go to finance, not here.”">
        <Textarea rows={2} value={f.notes} onChange={set("notes")} />
      </Field>
      <ChannelRows channels={channels} setChannels={setChannels} legend="Channels that belong to this project" />
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#2A2927] pt-4">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" className="size-4 accent-[#FF4F1A]" checked={createPlane} onChange={(e) => setCreatePlane(e.target.checked)} />
          also create the project in Plane
        </label>
        <Button type="submit" disabled={save.isPending || taken || !slug}>
          {save.isPending ? "Creating…" : "Create project"}
        </Button>
      </div>
    </form>
  );
}

function CompanyEditor({ id, c, template, onSaved }: { id: string; c?: Company; template: string; onSaved: (file: string) => void }) {
  const [text, setText] = useState(c?.content ?? (template || "# Organisation\n"));
  const [file, setFile] = useState(c?.file ?? "");
  const save = useAgentAction<void>(id, "company_save", () => ({ file, content: text }), { refresh: ["context_list"], success: "Saved" });
  return (
    <>
      <EditorTitle title={c ? c.name : "New company note"} badge={c && <span className="font-mono text-xs text-[#A8A59D]">{c.file}</span>} />
      {!c && (
        <Field label="File name" hint="e.g. acme.md">
          <Input className="h-11 font-mono" value={file} onChange={(e) => setFile(e.target.value.toLowerCase().replace(/[^a-z0-9.-]/g, ""))} required />
        </Field>
      )}
      <label className="flex flex-col gap-1.5">
        <span className={label11}>The note</span>
        <Textarea
          rows={20}
          className="bg-[#0E0E0D] px-4 py-3.5 font-mono text-[13px] leading-[1.7]"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <span className="text-xs text-[#A8A59D]">Its rules section overrides the defaults for that company's work.</span>
      </label>
      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-[#2A2927] pt-4">
        <Note>The next run uses it · saved as a git commit</Note>
        <Button disabled={save.isPending || !file.endsWith(".md")} onClick={() => save.mutate(undefined, { onSuccess: () => onSaved(file) })}>
          Save
        </Button>
      </div>
    </>
  );
}

function ChannelsEditor({ id, text }: { id: string; text: string }) {
  const [value, setValue] = useState(text);
  const save = useAgentAction<void>(id, "channels_save", () => ({ content: value }), { refresh: ["context_list"], success: "Saved" });
  return (
    <>
      <EditorTitle title="Channel map" badge={<span className="font-mono text-xs text-[#A8A59D]">channels.yaml</span>} />
      <p className="m-0 text-[#CFCBC1]">
        Which inbox, workspace, chat or repo belongs to which project. Usually you map channels from a project instead.
      </p>
      <Textarea
        rows={22}
        className="bg-[#0E0E0D] px-4 py-3.5 font-mono text-[13px] leading-[1.7]"
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-[#2A2927] pt-4">
        <Note>Checked before saving; the next run uses it</Note>
        <Button disabled={save.isPending} onClick={() => save.mutate()}>
          Save
        </Button>
      </div>
    </>
  );
}

function ProjectsTab() {
  const [sel, setSel] = useState<Sel | null>(null);
  return (
    <ServerPage
      maxWidth={1240}
      title="Projects"
      subtitle={
        <span className="block max-w-[72ch]">
          Each project is a note the agent reads every run, plus a project in Plane. Channels you map to a project send their
          messages there.
        </span>
      }
      actions={<Button onClick={() => setSel({ kind: "new-project" })}>New project</Button>}
    >
      <ProjectsTabBody sel={sel} setSel={setSel} />
    </ServerPage>
  );
}
